import styled from 'styled-components';
import { theme } from '../theme';

// TikTok-style profile navigation
export const ProfileNavigationContainer = styled.div`
  /* SCROLLS WITH THE PROFILE, rather than hovering over it.
     
     This was position: sticky with a translucent background and a 10px
     backdrop blur, so the tab bar detached from the content and the profile
     slid underneath it — the "floating" look. Worse in the peek drawer, which
     is where other people's profiles are usually read: the negative margins
     below pulled the bar 2rem past each edge of a panel that has no padding
     to absorb them, so it bled outside the drawer as well as floating over it.
     
     The blur went with the stickiness. A backdrop filter has nothing to blur
     once the bar is in the flow, and it creates a stacking context and
     containing block for anything positioned inside it. */
  background: var(--bg-card);
  border-bottom: 1px solid ${theme.colors.border};
  /* Margins kept vertical only. Full-bleed by negative margin assumes a
     particular parent padding, and this component is rendered in two places
     with different padding. */
  margin: ${theme.spacing.lg} 0 0 0;
  padding: 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    margin: ${theme.spacing.md} 0 0 0;
    -webkit-overflow-scrolling: touch;
  }
`;

export const ProfileNavTabs = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  position: relative;
  padding: 0;
  gap: ${theme.spacing.sm};
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  
  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 0;
    /* Ensure tabs don't shrink too much on mobile */
    justify-content: flex-start;
    padding: 0 ${theme.spacing.xs};
  }
`;

export const ProfileNavTab = styled.button<{ $isActive: boolean }>`
  flex: 1 1 auto;
  min-width: fit-content;
  background: transparent;
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-size: 0.9rem;
  font-weight: ${props => props.$isActive ? '700' : '600'};
  color: ${props => props.$isActive ? 'var(--text-primary)' : 'var(--text-secondary)'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent; /* Remove tap highlight on mobile */
  user-select: none;
  
  &:hover {
    color: var(--text-primary);
  }
  
  /* Active touch feedback on mobile */
  &:active {
    transform: scale(0.95);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%) scaleX(${props => props.$isActive ? '1' : '0'});
    width: 60%;
    height: 2px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex: 1 1 0;
    min-width: 70px;
    padding: ${theme.spacing.md} ${theme.spacing.sm};
    font-size: 0.8rem;
    
    &::after {
      width: 80%;
      height: 2.5px; /* Slightly thicker on mobile for better visibility */
    }
  }
  
  @media (max-width: 400px) {
    min-width: 60px;
    padding: ${theme.spacing.sm} ${theme.spacing.xs};
    font-size: 0.75rem;
    gap: 3px;
  }
`;

export const TabIcon = styled.span`
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  
  svg {
    width: 20px;
    height: 20px;
    color: inherit;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    svg {
      width: 22px;
      height: 22px;
    }
  }
  
  @media (max-width: 400px) {
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

export const TabLabel = styled.span`
  line-height: 1;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
  }
  
  @media (max-width: 380px) {
    font-size: 0.75rem;
  }
  
  @media (max-width: 350px) {
    display: none; /* Hide labels on very small screens, show only icons */
  }
`;

// Content sections that will be shown/hidden based on active tab
export const ProfileTabContent = styled.div<{ $isVisible: boolean }>`
  display: ${props => props.$isVisible ? 'block' : 'none'};
  animation: ${props => props.$isVisible ? 'fadeIn 0.3s ease-in' : 'none'};
  padding: ${theme.spacing.lg} 0;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md} 0;
  }
`;

// Section headers for each tab content
export const ProfileSectionHeader = styled.div`
  margin-top: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-top: ${theme.spacing.lg};
    margin-bottom: ${theme.spacing.md};
  }
`;

export const ProfileSectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 ${theme.spacing.xs} 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

export const ProfileSectionSubtitle = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.85rem;
  }
`;

